import axios, { AxiosHeaders, AxiosResponse } from 'axios';
import { CustomInternalAxiosRequestConfig } from '@/types/http';
import { getAccessToken, shouldRefreshToken, removeTokens } from '@/services/token';
import { ErrorCode, HTTP_STATUS_TO_ERROR_CODE } from '@/types/error';

// バックエンドのURL
const API_URL = process.env.NEXT_PUBLIC_API_URL;

// ログイン画面へリダイレクト（重複実行防止）
let isRedirecting = false;
let redirectTimeoutId: ReturnType<typeof setTimeout> | null = null;

// リダイレクト中かどうかを確認する関数（外部から参照用）
export const isRedirectingToLogin = () => isRedirecting;

export const redirectToLogin = () => {
  if (isRedirecting) return;
  if (typeof window === 'undefined') return;

  // 既にログインページにいる場合はリダイレクトしない
  if (window.location.pathname === '/login') return;

  isRedirecting = true;

  // 5秒後にフラグをリセット（遷移が完了しなかった場合の安全策）
  if (redirectTimeoutId) clearTimeout(redirectTimeoutId);
  redirectTimeoutId = setTimeout(() => {
    isRedirecting = false;
    redirectTimeoutId = null;
  }, 5000);

  const currentPath = window.location.pathname + window.location.search;
  const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}&message=session_expired`;
  window.location.href = loginUrl;
};

// トークンリフレッシュ処理を一元管理
// 重複排除はrefreshTokens()内部で行われるため、ここでは動的インポートのみ担当
const performTokenRefresh = async (): Promise<boolean> => {
  // 動的インポートで循環参照を回避
  const { refreshTokens } = await import('@/services/auth');
  return await refreshTokens();
};

// APIリクエスト用のaxiosインスタンスを作成
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000, // タイムアウトを30秒に設定（ネットワーク遅延対策）
});

// リクエストインターセプター
api.interceptors.request.use(
  async (config: CustomInternalAxiosRequestConfig) => {
    const newConfig = { ...config };

    // トークンチェックをスキップするパスのリスト
    const skipAuthPaths = [
      '/login/',
      '/register/',
      '/token/refresh/',
      '/auth/check-email/',
      '/auth/check-username/',
      // 必要に応じて他の認証不要なパスを追加
    ];

    // トークンチェックが必要かどうかを判定
    const shouldCheckToken = !skipAuthPaths.some(path =>
      config.url?.endsWith(path)
    ) && !newConfig.skipTokenRefresh;

    // トークンの更新が必要かチェック
    if (shouldCheckToken && shouldRefreshToken()) {
      const refreshSuccess = await performTokenRefresh();
      if (!refreshSuccess) {
        // トークン更新に失敗した場合
        removeTokens();
        // セッション切れの場合はログイン画面にリダイレクト
        redirectToLogin();
        throw new Error(handleError('AUTH_EXPIRED'));
      }
    }

    // 更新後の最新のアクセストークンを取得
    const token = getAccessToken();

    if (!newConfig.headers) {
      newConfig.headers = new AxiosHeaders();
    }

    if (token) {
      newConfig.headers.set('Authorization', `Bearer ${token}`);
    }

    if (!newConfig.headers.has('Content-Type')) {
      newConfig.headers.set('Content-Type', 'application/json');
    }

    return newConfig;
  },
  (error: unknown) => {
    console.error('リクエストインターセプターエラー:', error);
    return Promise.reject(new Error(handleError('REQUEST_FAILED')));
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config) {
      return Promise.reject(new Error(handleError('UNKNOWN_ERROR')));
    }

    const originalRequest = error.config as CustomInternalAxiosRequestConfig;
    const MAX_RETRY_COUNT = 2; // 最大リトライ回数

    if (typeof originalRequest._retryCount === 'undefined') {
      originalRequest._retryCount = 0;
    }

    // 401エラーの処理（認証エラー）
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest._retryCount < MAX_RETRY_COUNT &&
      !originalRequest.url?.endsWith('/token/refresh/') // リフレッシュエンドポイント自体の401は除外
    ) {
      try {
        originalRequest._retry = true;
        originalRequest._retryCount++;

        // performTokenRefresh関数を使用して一元化
        const refreshSuccess = await performTokenRefresh();
        if (!refreshSuccess) {
          removeTokens();
          // セッション切れの場合はログイン画面にリダイレクト
          redirectToLogin();
          return Promise.reject(new Error(handleError('AUTH_EXPIRED')));
        }

        // 新しいアクセストークンでリクエストヘッダーを更新
        const newAccessToken = getAccessToken();
        if (!originalRequest.headers) {
          originalRequest.headers = new AxiosHeaders();
        }
        originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);

        // リクエストの再試行
        return await api.request(originalRequest);
      } catch {
        removeTokens();
        // セッション切れの場合はログイン画面にリダイレクト
        redirectToLogin();
        return Promise.reject(new Error(handleError('AUTH_REFRESH_FAILED')));
      }
    }

    // その他のエラー処理
    if (error.response) {
      const errorCode = HTTP_STATUS_TO_ERROR_CODE[error.response.status] || 'UNKNOWN_ERROR';

      // 404エラーはrejectせず、エラー情報を含むレスポンスとして返す
      if (error.response.status === 404) {
        return {
          data: null,
          status: 404,
          statusText: 'Not Found',
          headers: error.response.headers,
          config: error.config,
          error: errorCode
        };
      }

      return Promise.reject(new Error(handleError(errorCode)));
    }

    if (error.request) {
      return Promise.reject(new Error(handleError('NETWORK_ERROR')));
    }

    return Promise.reject(new Error(handleError('REQUEST_FAILED')));
  }
);

// エラーハンドリング関数
const handleError = (errorCode: ErrorCode) => {
  return errorCode;
};
